import { confirmDialog } from "./confirmDialog";
import { ingestScorePayload } from "./scores";
import { displayDecision, verdictTier } from "./wheelScreenerLayout";

export const STOCKFORGE_ORIGIN = "https://thestockforge.com";
export const STOCKFORGE_ANALYZE_URL = `${STOCKFORGE_ORIGIN}/dashboard#analyze`;

const MODAL_ID = "sfe-stockforge-report-modal";

interface StockForgeReportPayload {
  ticker?: string;
  decision?: string;
  normalized_score?: number;
  created_at?: string;
  markdown?: string;
  report?: { markdown?: string; data?: { markdown?: string } };
  data?: { markdown?: string };
}

declare global {
  interface Window {
    marked?: { parse: (src: string) => string };
  }
}

function esc(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function normalizeTicker(ticker: string): string {
  return ticker.trim().toUpperCase();
}

function fmtWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function reportMarkdown(report: StockForgeReportPayload): string {
  return (
    report.markdown ??
    report.report?.markdown ??
    report.report?.data?.markdown ??
    report.data?.markdown ??
    ""
  );
}

async function ensureMarked(): Promise<Window["marked"]> {
  if (window.marked?.parse) return window.marked;

  const src =
    document.querySelector<HTMLScriptElement>('script[src*="marked"]')?.src ??
    `${STOCKFORGE_ORIGIN}/dashboard/assets/vendor/marked.min.js`;

  await new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Could not load markdown renderer"));
    document.head.appendChild(script);
  });

  if (!window.marked?.parse) {
    throw new Error("Markdown renderer unavailable");
  }
  return window.marked;
}

async function fetchReport(ticker: string): Promise<StockForgeReportPayload | null> {
  const path = `/v1/reports/${encodeURIComponent(ticker)}/latest`;
  const api = (window as Window & { API?: { get: (p: string, o?: { silent?: boolean }) => Promise<unknown> } })
    .API;

  if (api?.get) {
    try {
      return (await api.get(path, { silent: true })) as StockForgeReportPayload;
    } catch (err) {
      const status = (err as { status?: number }).status;
      if (status === 404) return null;
      throw err;
    }
  }

  const res = await fetch(path, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (res.status === 404) return null;
  if (!res.ok) {
    throw new Error(`Report fetch failed (${res.status})`);
  }
  return (await res.json()) as StockForgeReportPayload;
}

function ensureModal(): HTMLElement {
  let modal = document.getElementById(MODAL_ID);
  if (modal) return modal;

  modal = document.createElement("div");
  modal.id = MODAL_ID;
  modal.className = "modal hidden sfe-stockforge-report-modal";
  modal.setAttribute("role", "dialog");
  modal.setAttribute("aria-modal", "true");
  modal.setAttribute("aria-labelledby", "sfeReportTitle");
  modal.innerHTML = `
    <div class="modal-card sfe-stockforge-report-card">
      <div class="row" style="justify-content:space-between;align-items:flex-start;gap:12px;">
        <div class="row" style="gap:10px;align-items:center;min-width:0;">
          <h3 id="sfeReportTitle" style="margin:0;">Report</h3>
          <span class="pill" id="sfeReportScore"></span>
        </div>
        <div class="row" style="gap:8px;align-items:center;">
          <a id="sfeReportPdf" class="pill" href="#" target="_blank" rel="noopener">Download PDF</a>
          <a id="sfeReportExternal" class="pill" href="${STOCKFORGE_ORIGIN}/dashboard#history" target="_blank" rel="noopener">Open on Stock Forge</a>
          <button type="button" class="ghost small" id="sfeReportClose" aria-label="Close report">✕</button>
        </div>
      </div>
      <div id="sfeReportMeta" class="muted small" style="margin-top:4px;"></div>
      <div id="sfeReportState" class="state hidden" style="margin-top:12px;"></div>
      <div id="sfeReportBody" class="report-body-block" style="margin-top:12px;"></div>
    </div>`;

  modal.addEventListener("click", (event) => {
    if (event.target === modal) hideReportModal();
  });
  modal.querySelector("#sfeReportClose")?.addEventListener("click", () => hideReportModal());
  bindReportModalEscape();
  document.body.appendChild(modal);
  return modal;
}

function isReportModalOpen(): boolean {
  const modal = document.getElementById(MODAL_ID);
  return Boolean(modal && !modal.classList.contains("hidden"));
}

function onReportModalKeydown(event: KeyboardEvent): void {
  if (event.key !== "Escape") return;
  if (!isReportModalOpen()) return;
  if (document.getElementById("sfe-confirm-dialog-overlay")) return;
  event.preventDefault();
  event.stopPropagation();
  hideReportModal();
}

let reportEscapeBound = false;

function bindReportModalEscape(): void {
  if (reportEscapeBound) return;
  reportEscapeBound = true;
  document.addEventListener("keydown", onReportModalKeydown, true);
}

function showReportModal(): void {
  const modal = ensureModal();
  modal.classList.remove("hidden");
}

export function hideReportModal(): void {
  document.getElementById(MODAL_ID)?.classList.add("hidden");
}

function renderReportBody(ticker: string, report: StockForgeReportPayload, bodyEl: HTMLElement): void {
  const md = reportMarkdown(report);
  if (!md) {
    bodyEl.innerHTML = "<p class='muted'>No detailed report text available.</p>";
    return;
  }

  void ensureMarked()
    .then((marked) => {
      bodyEl.className = "report-body-block";
      bodyEl.innerHTML = `<div class="report-md">${marked.parse(md)}</div>`;
    })
    .catch(() => {
      bodyEl.className = "report-body-block";
      bodyEl.innerHTML = `<pre class="report-md">${esc(md)}</pre>`;
    });
}

function paintReport(ticker: string, report: StockForgeReportPayload): void {
  const modal = ensureModal();
  const titleEl = modal.querySelector<HTMLElement>("#sfeReportTitle");
  const scoreEl = modal.querySelector<HTMLElement>("#sfeReportScore");
  const metaEl = modal.querySelector<HTMLElement>("#sfeReportMeta");
  const stateEl = modal.querySelector<HTMLElement>("#sfeReportState");
  const bodyEl = modal.querySelector<HTMLElement>("#sfeReportBody");
  const pdfEl = modal.querySelector<HTMLAnchorElement>("#sfeReportPdf");
  if (!titleEl || !scoreEl || !metaEl || !stateEl || !bodyEl || !pdfEl) return;

  const score = report.normalized_score;
  const scoreTxt = typeof score === "number" ? `${Math.round(score)} / 100` : "";

  if (report.decision) {
    const tier = verdictTier(report.decision);
    titleEl.innerHTML = `${esc(ticker)} · <span class="wheel-rubric-${tier} sfe-report-rubric-label">${esc(displayDecision(report.decision))}</span>`;
    scoreEl.className = `pill report-score-pill report-score-${tier}`;
  } else {
    titleEl.textContent = ticker;
    scoreEl.className = "pill report-score-pill";
  }
  scoreEl.textContent = scoreTxt;

  metaEl.textContent = report.created_at ? `Last updated ${fmtWhen(report.created_at)}` : "";
  pdfEl.href = `/v1/reports/${encodeURIComponent(ticker)}/latest/pdf`;

  stateEl.classList.add("hidden");
  stateEl.innerHTML = "";
  bodyEl.innerHTML = "";
  renderReportBody(ticker, report, bodyEl);
}

function paintReportError(ticker: string, message: string): void {
  const modal = ensureModal();
  const titleEl = modal.querySelector<HTMLElement>("#sfeReportTitle");
  const stateEl = modal.querySelector<HTMLElement>("#sfeReportState");
  const bodyEl = modal.querySelector<HTMLElement>("#sfeReportBody");
  if (!titleEl || !stateEl || !bodyEl) return;

  titleEl.textContent = ticker;
  stateEl.classList.remove("hidden");
  stateEl.innerHTML = `<p class="muted">${esc(message)}</p>`;
  bodyEl.innerHTML = "";
}

/** Open the fundamental report modal (same data source as thestockforge.com). */
export async function openStockForgeReport(ticker: string): Promise<void> {
  const tk = normalizeTicker(ticker);
  if (!tk) return;

  const modal = ensureModal();
  const titleEl = modal.querySelector<HTMLElement>("#sfeReportTitle");
  const scoreEl = modal.querySelector<HTMLElement>("#sfeReportScore");
  const metaEl = modal.querySelector<HTMLElement>("#sfeReportMeta");
  const stateEl = modal.querySelector<HTMLElement>("#sfeReportState");
  const bodyEl = modal.querySelector<HTMLElement>("#sfeReportBody");
  const pdfEl = modal.querySelector<HTMLAnchorElement>("#sfeReportPdf");
  if (!titleEl || !scoreEl || !metaEl || !stateEl || !bodyEl || !pdfEl) return;

  titleEl.textContent = tk;
  scoreEl.className = "pill";
  scoreEl.textContent = "";
  metaEl.textContent = "";
  bodyEl.innerHTML = "";
  pdfEl.href = `/v1/reports/${encodeURIComponent(tk)}/latest/pdf`;
  stateEl.classList.remove("hidden");
  stateEl.innerHTML = '<div class="spinner"></div>Loading report…';
  showReportModal();

  try {
    const report = await fetchReport(tk);
    if (!report) {
      hideReportModal();
      openStockForgeAnalyze(tk);
      return;
    }
    ingestScorePayload({ ticker: tk, ...report });
    paintReport(tk, report);
  } catch (err) {
    const message = err instanceof Error ? err.message : "Could not load report.";
    paintReportError(tk, message);
  }
}

/** Route tickers without a stored report to Stock Forge analyze (native wheel behavior). */
export function openStockForgeAnalyze(ticker: string): void {
  void openStockForgeAnalyzeAsync(ticker);
}

async function openStockForgeAnalyzeAsync(ticker: string): Promise<void> {
  const tk = normalizeTicker(ticker);
  if (!tk) return;

  const ok = await confirmDialog({
    title: "No report yet",
    message: `No report for ${tk} yet. Open Stock Forge to run a new analysis?`,
    confirmLabel: "Open Stock Forge",
  });
  if (!ok) return;

  window.open(STOCKFORGE_ANALYZE_URL, "_blank", "noopener,noreferrer");
}

function tickerFromChip(el: HTMLElement): string {
  const fromData = el.dataset.ticker?.trim() ?? "";
  if (fromData && fromData !== "—") return normalizeTicker(fromData);
  const text = (el.textContent ?? "").replace(/[↗→]/g, "").trim();
  if (!text || text === "—") return "";
  return normalizeTicker(text.split(/\s+/)[0] ?? "");
}

function onStockForgeTickerClick(event: Event): void {
  const target = event.target;
  if (!(target instanceof HTMLElement)) return;

  const rubricLink = target.closest<HTMLElement>(".wheel-rubric-link");
  if (rubricLink?.dataset.ticker) {
    event.preventDefault();
    event.stopPropagation();
    rubricLink.blur();
    void openStockForgeReport(rubricLink.dataset.ticker);
    return;
  }

  const rubricMissing = target.closest<HTMLElement>(".wheel-rubric-missing");
  if (rubricMissing?.dataset.ticker) {
    event.preventDefault();
    event.stopPropagation();
    rubricMissing.blur();
    openStockForgeAnalyze(rubricMissing.dataset.ticker);
    return;
  }

  if (target.closest("button, a, input, textarea, select, .sfe-advice-dot, .sfe-focus-row-menu")) {
    return;
  }

  const chip = target.closest<HTMLElement>(".recs-ticker");
  if (!chip) return;
  const ticker = tickerFromChip(chip);
  if (!ticker) return;
  event.preventDefault();
  event.stopPropagation();
  void openStockForgeReport(ticker);
}

let documentTickerLinksBound = false;

/** Ticker chips (↗) and rubric bands open the Stock Forge report. */
export function bindStockForgeTickerLinks(root: Document | HTMLElement = document): void {
  if (root instanceof Document || root === document) {
    if (documentTickerLinksBound) return;
    documentTickerLinksBound = true;
    document.addEventListener("click", onStockForgeTickerClick, true);
    return;
  }
  if (root.dataset.sfeRubricBound === "1") return;
  root.dataset.sfeRubricBound = "1";
  root.addEventListener("click", onStockForgeTickerClick);
}

/** Delegated clicks for native rubric band buttons rendered by StockForge+ web screeners. */
export function bindStockForgeRubricLinks(root: HTMLElement): void {
  bindStockForgeTickerLinks(root);
}
